export type ClientErrorCode = 400 | 401 | 403 | 404 | 429 // 409 - conflict (shared resource update failure)
export type ServerErrorCode = 500
export type ErrorCode = ClientErrorCode | ServerErrorCode

export interface SerializedError {
  name: string
  message: string
  stack?: string
  cause?: SerializedError
  code?: ErrorCode
  status?: number
  data?: unknown
}

export class RpcError extends Error {
  code: ErrorCode
  status: number
  data?: unknown
  serverStack?: string

  constructor(
    code: ErrorCode,
    message: string,
    options?: {
      cause?: unknown
      status?: number
      data?: unknown
      serverStack?: string
    },
  ) {
    super(message, { cause: options?.cause })
    this.name = 'RpcError'
    this.code = code
    this.status = options?.status ?? code
    this.data = options?.data
    this.serverStack = options?.serverStack
  }
}

export function toRpcError(error: unknown): RpcError {
  if (error instanceof RpcError) return error
  return new RpcError(
    500,
    error instanceof Error ? error.message : 'Internal server error',
    error instanceof Error ? { cause: error } : undefined,
  )
}

export function serializeRpcError(error: unknown): SerializedError {
  return serializeError(toRpcError(error), new Set())
}

export function deserializeRpcError(
  value: unknown,
  fallbackStatus = 500,
): RpcError {
  const serialized = normalizeSerializedError(value)
  if (!serialized) {
    return new RpcError(500, `HTTP ${fallbackStatus}`, {
      status: fallbackStatus,
    })
  }
  const code = isErrorCode(serialized.code) ? serialized.code : 500
  const status = isHttpErrorStatus(serialized.status)
    ? serialized.status
    : fallbackStatus
  const cause = serialized.cause
    ? deserializeError(serialized.cause)
    : undefined
  const error = new RpcError(code, serialized.message || `HTTP ${status}`, {
    cause,
    status,
    data: serialized.data,
    serverStack: serialized.stack,
  })
  applyRemoteErrorMetadata(error, serialized)
  return error
}

function serializeError(error: unknown, seen: Set<object>): SerializedError {
  if (!(error instanceof Error)) {
    return { name: 'NonError', message: String(error) }
  }
  if (seen.has(error)) {
    return { name: 'Error', message: 'Circular error cause' }
  }
  seen.add(error)
  const serialized: SerializedError = {
    name: error.name,
    message: error.message,
    ...(error.stack ? { stack: error.stack } : {}),
  }
  if (error instanceof RpcError) {
    serialized.code = error.code
    serialized.status = error.status
    const data = cloneJsonValue(error.data)
    if (data !== undefined) serialized.data = data
  }
  if (error.cause !== undefined) {
    serialized.cause = serializeError(error.cause, seen)
  }
  return serialized
}

function deserializeError(value: SerializedError): Error {
  const cause = value.cause ? deserializeError(value.cause) : undefined
  const error = isErrorCode(value.code)
    ? new RpcError(value.code, value.message, {
        cause,
        status: isHttpErrorStatus(value.status) ? value.status : value.code,
        data: value.data,
        serverStack: value.stack,
      })
    : new Error(value.message, { cause })
  applyRemoteErrorMetadata(error, value)
  return error
}

function applyRemoteErrorMetadata(error: Error, value: SerializedError): void {
  error.name = value.name
  if (!value.stack) return
  Object.defineProperty(error, 'serverStack', {
    configurable: true,
    enumerable: true,
    value: value.stack,
  })
  error.stack = value.stack
}

function cloneJsonValue(value: unknown): unknown {
  if (value === undefined) return undefined
  try {
    return JSON.parse(JSON.stringify(value)) as unknown
  } catch {
    return { serializationError: 'RpcError data is not JSON-serializable' }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeSerializedError(value: unknown): SerializedError | null {
  if (!isRecord(value) || typeof value.message !== 'string') return null
  const cause =
    value.cause === undefined
      ? undefined
      : normalizeSerializedError(value.cause)
  return {
    name:
      typeof value.name === 'string'
        ? value.name
        : isErrorCode(value.code)
          ? 'RpcError'
          : 'Error',
    message: value.message,
    ...(typeof value.stack === 'string' ? { stack: value.stack } : {}),
    ...(cause ? { cause } : {}),
    ...(isErrorCode(value.code) ? { code: value.code } : {}),
    ...(isHttpErrorStatus(value.status) ? { status: value.status } : {}),
    ...('data' in value ? { data: value.data } : {}),
  }
}

function isErrorCode(value: unknown): value is ErrorCode {
  return (
    value === 400 ||
    value === 401 ||
    value === 403 ||
    value === 404 ||
    value === 429 ||
    value === 500
  )
}

function isHttpErrorStatus(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 400 &&
    value <= 599
  )
}
